import type { PipelineStageDto, VenuePipelineDto } from '@lib/dto/pipeline.dto'
import { FUNNEL_PRICED_TARGETS, FUNNEL_TARGET_LABELS } from '@lib/enums/funnel-stage.enum'
import { dollarsFromCents } from '@lib/helpers/format'
import { Change, changeOf, InfoNote, Sparkline, StatCost, StatTile, StatValue, wholePercent } from '@/components/stats/StatsPieces'
import { formatCount, formatMicros } from '@/lib/adFormat'
import { formatReturn } from './SourceNumbers'

/** What the headline numbers read from a pipeline: a venue's or the company's. */
export type PipelineTotals = Pick<VenuePipelineDto, 'compare' | 'stages' | 'spend' | 'cost' | 'sourceSummary' | 'compared' | 'averageBookingCents'>

/** "Up $460 (6%)", "Down $800", "No change". */
function moneyChangeText(diff: number, before: number, format: (n: number) => string): string {
  if (diff === 0) return 'No change'
  const pct = before > 0 ? ` (${wholePercent(Math.abs(diff) / before)})` : ''
  return `${diff > 0 ? 'Up' : 'Down'} ${format(Math.abs(diff))}${pct}`
}

/** A change in money, neither good nor bad in itself: "Up $460 (6%) vs $7,960". */
function MoneyChange({ now, before, format }: { now: number; before: number | null; format: (n: number) => string }) {
  if (before === null) return <p className="m-0 text-small text-ink-muted">No comparison</p>
  const diff = now - before
  const said = moneyChangeText(diff, before, format)
  return (
    <p className="m-0 text-small text-ink-muted">
      <span className="font-medium text-ink">{said}</span> vs {format(before)}
    </p>
  )
}

/** A priced count's cost, or why it has none. */
function costLine(stage: PipelineStageDto, currency: string): string | null {
  if (stage.value === null || stage.value === 0) return null
  return stage.costPerMicros === null ? 'Too few to price' : `${formatMicros(stage.costPerMicros, currency)} each`
}

function CountTile({ stage, currency }: { stage: PipelineStageDto; currency: string | null }) {
  const label = FUNNEL_TARGET_LABELS[stage.target]
  const cost = currency === null ? null : costLine(stage, currency)
  return (
    <StatTile label={label} stillMoving={stage.stillMoving}>
      <StatValue>{formatCount(stage.value ?? 0)}</StatValue>
      <Change
        change={changeOf(stage.value, stage.previous)}
        against={stage.previous === null ? null : formatCount(stage.previous)}
        sameAge={stage.sameAge}
      />
      <Sparkline points={stage.trend} label={label} />
      {cost === null ? null : <StatCost>{cost}</StatCost>}
    </StatTile>
  )
}

/** What marketing cost, against the weeks compared with, with its listing fees' part. */
function CostTile({ data }: { data: PipelineTotals }) {
  const cost = data.cost
  if (cost === null) return null
  const format = (micros: number): string => formatMicros(micros, cost.currencyCode)
  const before = data.compared?.currencyCode === cost.currencyCode ? data.compared.costMicros : null
  const ads = data.spend?.currencyCode === cost.currencyCode ? data.spend.spendMicros : null
  const parts = [ads === null ? null : `Google Ads ${format(ads)}`, cost.listingsMicros === null ? null : `listings ${format(cost.listingsMicros)}`]
  return (
    <StatTile label="Marketing cost" stillMoving={false}>
      <StatValue>{format(cost.micros)}</StatValue>
      <MoneyChange now={cost.micros} before={before} format={format} />
      <StatCost>{parts.filter((p) => p !== null).join(' · ') || 'Ads and listing fees'}</StatCost>
    </StatTile>
  )
}

/** What the span's couples booked, and the return on what marketing cost. */
function ValueTile({ data }: { data: PipelineTotals }) {
  const summary = data.sourceSummary
  if (summary === null) return null
  const value = summary.bookedValueCents
  const ratio = summary.returnRatio
  const before = data.compared?.returnRatio ?? null
  return (
    <StatTile label="Booked value" stillMoving={false}>
      {value === null ? <StatValue missing>Not recorded</StatValue> : <StatValue>{dollarsFromCents(value)}</StatValue>}
      <p className="m-0 text-small text-ink-muted">
        {ratio === null ? 'No return to show' : <span className="font-medium text-ink">{formatReturn(ratio)}</span>}
        {ratio !== null && before !== null ? ` vs ${formatReturn(before)}` : null}
      </p>
      <StatCost>{data.averageBookingCents === null ? 'No booking value recorded' : `Average booking ${dollarsFromCents(data.averageBookingCents)}`}</StatCost>
    </StatTile>
  )
}

/** Why the return is missing or partial, said as the rest of the app says it. */
function ReturnNotes({ data, whose }: { data: PipelineTotals; whose: 'venue' | 'company' }) {
  const missing = data.sourceSummary?.missing ?? null
  if (missing === null) return null
  if (missing === 'no_values') {
    return (
      <InfoNote>
        {whose === 'venue'
          ? 'Booked value isn’t recorded here: this venue doesn’t send its proposals and contracts from FLOH Venues.'
          : 'Booked value isn’t recorded: none of the venues sends its proposals and contracts from FLOH Venues.'}
      </InfoNote>
    )
  }
  if (missing === 'currencies') return <InfoNote>Costs are in more than one currency, so there’s no return.</InfoNote>
  return <InfoNote>The return counts only the venues that record booked value, against their own costs.</InfoNote>
}

/**
 * The numbers a marketer reports (docs/pipeline-view.spec.md, "The headline
 * numbers"): what marketing cost, inquiries, tours and bookings each with its
 * cost, and what was booked against it. The money only for whoever sees ad
 * costs, on All couples; a count nothing records isn't there.
 */
export function PipelineTiles({ data, whose = 'venue' }: { data: PipelineTotals; whose?: 'venue' | 'company' }) {
  const currency = data.cost?.currencyCode ?? data.spend?.currencyCode ?? null
  const counts = FUNNEL_PRICED_TARGETS.flatMap((target) => data.stages.filter((s) => s.target === target && s.tracked))
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-3">
        <CostTile data={data} />
        {counts.map((stage) => (
          <CountTile key={stage.target} stage={stage} currency={currency} />
        ))}
        <ValueTile data={data} />
      </div>
      <ReturnNotes data={data} whose={whose} />
    </div>
  )
}
